import DeleteIcon from '@mui/icons-material/Delete'
import { Button, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import type { DiscountItemObject } from '../api/api'

export interface DiscountItemsProps {
  items: DiscountItemObject[]
  isLoading: boolean
  onDeleteDiscount: (id: DiscountItemObject['id']) => Promise<void>
  format: {
    discount: (discount: DiscountItemObject['discount']) => string | undefined
    price: (price: NonNullable<DiscountItemObject['price']>) => string | undefined
    expirationDate: (expirationDate: DiscountItemObject['discount']['expirationDate']) => string
  }
  getDiscountLabel: (discountType: string) => string
}

export const DiscountItems = ({ items, isLoading, onDeleteDiscount, format, getDiscountLabel }: DiscountItemsProps) => {
  if (isLoading) {
    return <div data-testid="items-skeleton" />
  }

  if (items.length === 0) {
    return (
      <Stack
        sx={{
          py: 1,
        }}
      >
        <Typography variant="p3Regular">There are no discounts for this customer</Typography>
      </Stack>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="p3SemiBold">Product name</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="p3SemiBold">Price</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="p3SemiBold">Price type</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="p3SemiBold">Discount type</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="p3SemiBold">Discount</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="p3SemiBold">Expires</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="p3SemiBold">Actions</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <Typography variant="p3Regular">{item.name}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="p3Regular">
                  {item.price
                    ? (format.price?.(item.price) ?? `${item.price.amount.value} ${item.price.amount.currency}`)
                    : 'Unknown price'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="p3Regular">{item.price ? item.price.type : 'Unknown price type'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="p3Regular">{getDiscountLabel(item.discount.type)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="p3Regular">
                  {format.discount?.(item.discount) ?? `${item.discount.value} ${item.discount.adornment}`}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="p3Regular">
                  {format.expirationDate?.(item.discount.expirationDate) ??
                    (item.discount.expirationDate ? new Date(item.discount.expirationDate).toLocaleDateString() : 'No expiration')}
                </Typography>
              </TableCell>
              <TableCell>
                <Button data-testid={`delete-button-${item.id}`} color="error" onClick={() => onDeleteDiscount(item.id)}>
                  <DeleteIcon />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
